<template>
  <div class="comment-item" :class="{ 'author-comment': isThreadAuthor }">
    <div class="comment-main">
      <div class="comment-header">
        <!-- Profile picture -->
        <div class="profile-pic">
          <img v-if="comment.profile_picture" :src="comment.profile_picture" alt="Profile picture" />
          <div v-else class="default-avatar">
            <i class="material-icons">person</i>
          </div>
        </div>
        
        <!-- User info section -->
        <div class="user-info">
          <div class="user-name-container">
            <span class="comment-author typo-subtitle-2">
              {{ comment.name }}
              <span v-if="isThreadAuthor" class="author-badge">Autor del hilo</span>
            </span>
          </div>
          <!-- Visual reputation indicator -->
          <div class="reputation-indicator" :class="reputationClass">
            <div 
              v-for="n in 5" 
              :key="n" 
              class="reputation-dot"
              :class="{ 
                'filled': n <= Math.abs(reputationValue),
                'negative': reputationValue < 0 
              }"
            ></div>
          </div>
        </div>
        
        <!-- Comment metadata -->
        <div class="comment-meta">
          <span class="comment-date typo-caption">
            {{ formatTimestamp(comment.created) }}
          </span>
          <!-- Show views for OP only -->
          <span v-if="comment.cid === 'op' && comment.views !== undefined && isAdmin" class="comment-views typo-caption">
            <i class="material-icons views-icon">visibility</i>
            {{ comment.views }}
          </span>
          <div class="comment-actions-container">
            <span class="comment-number typo-caption" @click="quoteComment">#{{ commentNumber }}</span>
            <!-- Three dots menu -->
            <div class="comment-menu-container" v-if="canModifyContent">
              <button class="menu-trigger" @click.stop="toggleMenu">
                <i class="material-icons">more_vert</i>
              </button>
              <div class="dropdown-menu" v-if="showMenu">
                <button class="menu-item" @click="editMessage">
                  <i class="material-icons">edit</i> Editar
                </button>
                <button class="menu-item delete" @click="deleteMessage">
                  <i class="material-icons">delete</i> Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="comment-body typo-body-2">
        <!-- Quote for replies - cleaner design without double borders -->
        <div v-if="comment.pid !== '0'" class="comment-quote-container">
          <div 
            v-if="nestedQuoteChain" 
            class="quote-content"
            :class="{ 
              'expanded': isQuoteExpanded,
              'has-expanded-nested': hasExpandedNested
            }"
          >
            <div ref="quoteContentBody" class="nested-quote-chain">
              <!-- Render quotes recursively -->
              <div 
                v-for="(quote, index) in nestedQuoteChain" 
                :key="quote.cid"
                v-show="index === 0 || isNestedExpanded(index - 1)"
                class="nested-quote-level"
                :class="`quote-level-${index}`"
                :style="{ marginLeft: `${index * 16}px` }"
              >
                <div class="quote-author">
                  <div class="quote-avatar">
                    <span class="quote-icon">❝</span>
                  </div>
                  <div class="quote-author-info">
                    <strong>{{ quote.name }}</strong>
                    <button 
                      class="goto-message-btn"
                      @click="goToMessage(quote.cid)"
                      title="Ir al mensaje"
                    >
                      <i class="material-icons">launch</i>
                    </button>
                  </div>
                </div>
                <div class="quote-body-content" v-html="quote.content"></div>
                
                <!-- Show expand/collapse button if there are more quotes after this one -->
                <button 
                  v-if="index < nestedQuoteChain.length - 1"
                  class="toggle-nested-btn"
                  @click="toggleNestedQuote(index)"
                >
                  {{ isNestedExpanded(index) ? '▼' : '▶' }} 
                  {{ isNestedExpanded(index) ? 'Ocultar' : 'Mostrar' }} citas anteriores ({{ nestedQuoteChain.length - index - 1 }})
                </button>
              </div>
            </div>
            <button 
              v-if="isQuoteOverflowing"
              class="toggle-quote-btn"
              @click="isQuoteExpanded = !isQuoteExpanded"
            >
              {{ isQuoteExpanded ? 'Leer menos' : 'Leer más' }}
            </button>
          </div>
          <p v-else class="quote-fallback">
            Respuesta a otro comentario
          </p>
        </div>
        <div class="comment-body-content" v-html="comment.comment_body_value" @click="handleContentClick"></div>
        
        <!-- Display attached images -->
        <div v-if="comment.images && comment.images.length > 0" class="comment-images">
          <div 
            v-for="image in comment.images" 
            :key="image.fid" 
            class="image-container"
          >
            <img 
              :src="image.url" 
              :alt="image.alt || image.filename"
              :title="image.title || image.filename"
              class="attached-image"
              @click="openImageModal(image)"
            />
            <div class="image-caption" v-if="image.title">
              {{ image.title }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Edit form (shown when editing) -->
      <div v-if="isEditing" class="edit-form">
        <textarea 
          v-model="editedContent" 
          class="edit-textarea"
          rows="5"
        ></textarea>
        <div class="edit-actions">
          <button class="cancel-btn" @click="cancelEdit">Cancelar</button>
          <button class="save-btn" @click="saveEdit">Guardar</button>
        </div>
      </div>
      
      <!-- Add voting controls -->
      <div class="comment-actions">
        <div class="voting-controls">
          <button 
            class="vote-btn upvote" 
            :class="{ 'active': userVote > 0 }"
            @click="vote(1)"
            :disabled="voteLoading"
            title="Voto positivo"
          >
            <i class="material-icons">thumb_up</i>
            <span class="vote-count">{{ voteCount.positive }}</span>
          </button>
          
          <button 
            class="vote-btn downvote" 
            :class="{ 'active': userVote < 0 }"
            @click="vote(-1)"
            :disabled="voteLoading"
            title="Voto negativo"
          >
            <i class="material-icons">thumb_down</i>
            <span class="vote-count">{{ voteCount.negative }}</span>
          </button>
          
          <span class="total-score" :class="scoreClass">
            {{ voteCount.total > 0 ? '+' + voteCount.total : voteCount.total }}
          </span>
        </div>
        
        <!-- Need Response Button (Admin only) -->
        <button 
          v-if="isAdmin"
          class="need-response-btn" 
          :class="{ 'active': needsResponse }"
          @click="toggleNeedResponse"
          :disabled="needResponseLoading"
          title="Marcar como necesita respuesta"
        >
          <i class="material-icons">{{ needsResponse ? 'check_circle' : 'help_outline' }}</i>
          <span>{{ needsResponse ? 'No necesita respuesta' : 'Necesita respuesta' }}</span>
        </button>
        
        <button v-if="canWrite" class="reply-btn" @click="replyToComment">
          <i class="material-icons">reply</i>
          Responder
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/features/forum/services/forumApi';
import { mapGetters } from 'vuex';
const axios = forumApi;

export default {
  name: 'ForumMessage',
  props: {
    comment: {
      type: Object,
      required: true
    },
    parentComment: {
      type: Object,
      default: null
    },
    allComments: {
      type: Array,
      default: () => []
    },
    formatTimestamp: {
      type: Function,
      required: true
    },
    isThreadAuthor: {
      type: Boolean,
      default: false
    },
    commentIndex: {
      type: Number,
      default: 0
    },
    isFirstPage: {
      type: Boolean,
      default: true
    },
    userKarma: {
      type: Object,
      default: null
    },
    currentUserId: {
      type: Number,
      default: 0
    },
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showMenu: false,
      isEditing: false,
      editedContent: '',
      voteCount: {
        positive: 0,
        negative: 0,
        total: 0
      },
      userVote: null,
      voteLoading: false,
      needsResponse: false,
      needResponseLoading: false,
      isQuoteExpanded: false,
      isQuoteOverflowing: false,
      expandedNestedQuotes: {}
    };
  },
  computed: {
    ...mapGetters(['_g_User']),
    // Replace the mock reputation value with real karma data
    reputationValue() {
      if (this.userKarma) {
        return this.userKarma.normalized_karma;
      }
      return 0; // Default value while loading
    },
    // Get CSS class based on reputation
    reputationClass() {
      const rep = this.reputationValue;
      if (rep > 3) return 'reputation-high';
      if (rep > 0) return 'reputation-positive';
      if (rep === 0) return 'reputation-neutral';
      if (rep > -3) return 'reputation-negative';
      return 'reputation-low';
    },
    // Get comment number (1 for OP, incremental for others)
    commentNumber() {
      // If this is the original post, it's always #1
      if (this.comment.cid === 'op') {
        return 1;
      } 
      
      // For regular comments, just add 2 to the index
      // (since index is 0-based and OP is #1)
      return this.commentIndex + 2;
    },
    // Check if current user can modify this content
    canModifyContent() {
      // Only allow admins to modify content - rely on prop passed from parent for consistency
      return this.isAdmin;
    },
    scoreClass() {
      if (this.voteCount.total > 0) return 'positive-score';
      if (this.voteCount.total < 0) return 'negative-score';
      return '';
    },
    canWrite() {
      const user = this._g_User;
      const isAdminUser = !!(user && user.is_admin_user);
      const isWriter = !!(user && user.is_forum_writer_user);
      return isAdminUser || isWriter;
    },
    // Build the complete nested quote chain
    nestedQuoteChain() {
      if (!this.parentComment || !this.allComments.length) return null;
      
      const chain = [];
      let currentComment = this.parentComment;
      
      // Build chain by following parent relationships
      while (currentComment && chain.length < 5) { // Limit to 5 levels to prevent infinite loops
        chain.push({
          name: currentComment.name,
          content: currentComment.comment_body_value,
          cid: currentComment.cid
        });
        
        // Find the parent of the current comment
        if (currentComment.pid !== '0') {
          currentComment = this.allComments.find(c => c.cid === currentComment.pid);
        } else {
          break;
        }
      }
      
      return chain;
    },
    // Check if any nested quotes are expanded
    hasExpandedNested() {
      return Object.values(this.expandedNestedQuotes).some(expanded => expanded);
    }
  },
  methods: {
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    
    openImageModal(image) {
      // Simple image modal - open in new tab for now
      window.open(image.url, '_blank');
    },
    
    handleContentClick(event) {
      // Check if the clicked element is an image
      if (event.target.tagName === 'IMG') {
        event.preventDefault();
        window.open(event.target.src, '_blank');
      }
    },
    closeMenu() {
      this.showMenu = false;
    },
    editMessage() {
      this.editedContent = this.comment.comment_body_value;
      this.isEditing = true;
      this.closeMenu();
    },
    cancelEdit() {
      this.isEditing = false;
      this.editedContent = '';
    },
    saveEdit() {
      // Determine if this is a node (thread) or comment
      const contentType = this.comment.cid === 'op' ? 'node' : 'comment';
      
      // For original post, get nid from comment object or route params as fallback
      let contentId;
      if (contentType === 'node') {
        contentId = this.comment.nid || (this.$route && this.$route.params && this.$route.params.nid);
      } else {
        contentId = this.comment.cid;
      }
      
      // Make sure we have a valid content ID
      if (!contentId) {
        console.error('Missing content ID for editing', this.comment);
        this.$emit('message-updated', {
          type: 'error',
          text: 'Error: No se puede determinar el ID del contenido a editar'
        });
        return;
      }
      
      // Prepare data for the API
      const data = {
        body: this.editedContent
      };
      
      // If this is a thread, we might want to update the title too
      if (contentType === 'node' && this.editedTitle) {
        data.title = this.editedTitle;
      }
      
      // Call the API to update the content
      axios.post(`/forum/edit-content/${contentType}/${contentId}`, data)
        .then(response => {
          if (response.data.success) {
            // Update the local content
            this.comment.comment_body_value = response.data.content.body;
            this.isEditing = false;
            
            // Show success message
            this.$emit('message-updated', {
              type: 'success',
              text: 'Mensaje actualizado correctamente'
            });
          } else {
            // Show error message
            this.$emit('message-updated', {
              type: 'error',
              text: response.data.message || 'Error al actualizar el mensaje'
            });
          }
        })
        .catch(error => {
          console.error('Error updating content:', error);
          this.$emit('message-updated', {
            type: 'error',
            text: 'Error al actualizar el mensaje'
          });
        });
    },
    deleteMessage() {
      if (confirm('¿Estás seguro de que deseas eliminar este mensaje? Esta acción no se puede deshacer.')) {
        // Determine if this is a node (thread) or comment
        const contentType = this.comment.cid === 'op' ? 'node' : 'comment';
        
        // For original post, get nid from comment object or route params as fallback
        let contentId;
        if (contentType === 'node') {
          contentId = this.comment.nid || (this.$route && this.$route.params && this.$route.params.nid);
        } else {
          contentId = this.comment.cid;
        }
        
        // Make sure we have a valid content ID
        if (!contentId) {
          console.error('Missing content ID for deletion', this.comment);
          this.$emit('message-updated', {
            type: 'error',
            text: 'Error: No se puede determinar el ID del contenido a eliminar'
          });
          return;
        }
        
        // Call the API to delete the content
        axios.post(`/forum/delete-content/${contentType}/${contentId}`)
          .then(response => {
            if (response.data.success) {
              // Update the local content to show it's been deleted
              this.comment.comment_body_value = '[Este mensaje ha sido eliminado]';
              this.closeMenu();
              
              // Show success message
              this.$emit('message-updated', {
                type: 'success',
                text: 'Mensaje eliminado correctamente'
              });
            } else {
              // Show error message
              this.$emit('message-updated', {
                type: 'error',
                text: response.data.message || 'Error al eliminar el mensaje'
              });
            }
          })
          .catch(error => {
            console.error('Error deleting content:', error);
            this.$emit('message-updated', {
              type: 'error',
              text: 'Error al eliminar el mensaje'
            });
          });
      }
      this.closeMenu();
    },
    goToQuotedMessage() {
      if (this.parentComment) {
        this.$emit('goto-quoted-message', this.parentComment.cid);
      }
    },
    quoteComment() {
      this.$emit('quote-comment', this.comment, this.commentNumber);
    },
    replyToComment() {
      this.$emit('reply-to-comment', this.comment, this.commentNumber);
    },
    fetchVotes() {
      // For OP (original post), we need to make sure we have the thread ID (nid)
      if (this.comment.cid === 'op') {
        // If we don't have nid directly in the comment object, try to get it from the route
        if (!this.comment.nid && this.$route && this.$route.params && this.$route.params.nid) {
          this.comment.nid = this.$route.params.nid;
        }
        
        // If we still don't have a valid nid, we can't fetch votes
        if (!this.comment.nid) {
          console.error('Missing thread ID (nid) for OP vote fetch', this.comment);
          return;
        }
      }
      
      // Get entity type and ID
      const entityType = this.comment.cid === 'op' ? 'node' : 'comment';
      const entityId = this.comment.cid === 'op' ? this.comment.nid : this.comment.cid;
      
      // Make sure we have valid entity ID before making the request
      if (!entityId) {
        console.error('Missing entity ID for vote fetch', this.comment);
        return;
      }
      
      // Include include_users=1 to get information about the current user's vote
      axios.get(`/forum/api/votes?entity_type=${entityType}&entity_id=${entityId}&include_users=1`)
        .then(response => {
          if (response.data && response.data.success) {
            this.voteCount = response.data.vote_count;
            
            // Check if the user has already voted
            if (response.data.user_vote !== undefined) {
              this.userVote = response.data.user_vote;
            }
          }
        })
        .catch(err => {
          console.error('Error fetching votes:', err);
        });
    },
    vote(value) {
      // Don't allow voting if not logged in
      if (!this.$store.getters._g_IsUserLogged) {
        alert('Debes iniciar sesión para votar');
        return;
      }
      
      // For OP (original post), we need to make sure we have the thread ID (nid)
      if (this.comment.cid === 'op') {
        // If we don't have nid directly in the comment object, try to get it from the route
        if (!this.comment.nid && this.$route && this.$route.params && this.$route.params.nid) {
          this.comment.nid = this.$route.params.nid;
        }
        
        // If we still don't have a valid nid, we can't vote
        if (!this.comment.nid) {
          console.error('Missing thread ID (nid) for OP vote', this.comment);
          return;
        }
      }
      
      // Get entity type and ID
      const entityType = this.comment.cid === 'op' ? 'node' : 'comment';
      const entityId = this.comment.cid === 'op' ? this.comment.nid : this.comment.cid;
      
      // Make sure we have valid entity ID before making the request
      if (!entityId) {
        console.error('Missing entity ID for vote', this.comment);
        return;
      }
      
      this.voteLoading = true;
      
      // Make sure we're sending all required fields
      const voteData = {
        entity_type: entityType,
        entity_id: entityId,
        value: value
      };
      
      axios.post('/forum/api/vote', voteData)
        .then(response => {
          if (response.data && response.data.success) {
            // Update vote count with the response data
            this.voteCount = response.data.vote_count;
            
            // Update user's vote based on the action taken
            if (response.data.action === 'removed') {
              this.userVote = null;
            } else {
              this.userVote = value;
            }
          } else if (response.data && response.data.message) {
            console.error('Vote error:', response.data.message);
          }
        })
        .catch(err => {
          console.error('Error submitting vote:', err);
          if (err.response && err.response.data) {
            console.error('Server response:', err.response.data);
          }
        })
        .finally(() => {
          this.voteLoading = false;
        });
    },
    checkQuoteOverflow() {
      this.$nextTick(() => {
        const el = this.$refs.quoteContentBody;
        if (el) {
          this.isQuoteOverflowing = el.scrollHeight > el.clientHeight;
          
          // If the quote contains nested blockquotes, expand it by default
          if (el.querySelector('blockquote')) {
            this.isQuoteExpanded = true;
          }
        }
      });
    },
    toggleNeedResponse() {
      if (!this.isAdmin || this.needResponseLoading) return;
      
      this.needResponseLoading = true;
      const isOriginalPost = this.comment.cid === 'op';
      const postType = isOriginalPost ? 'topic' : 'comment';
      const topicId = this.$route.params.nid;
      const commentId = isOriginalPost ? null : this.comment.cid;
      
      // Prepare the request data
      const requestData = {
        value: !this.needsResponse,
        post_type: postType,
        topic_id: topicId
      };
      
      if (commentId) {
        requestData.comment_id = commentId;
      }
      
      // Send the request to the API
      axios.post('/forum/api/need-response/set', requestData)
        .then(response => {
          if (response.data && response.data.success) {
            this.needsResponse = !this.needsResponse;
            // Emit an event to notify the parent component
            this.$emit('need-response-changed', {
              id: isOriginalPost ? topicId : commentId,
              type: postType,
              needsResponse: this.needsResponse
            });
          } else {
            console.error('Error toggling need response:', response.data);
          }
        })
        .catch(error => {
          console.error('Error toggling need response:', error);
        })
        .finally(() => {
          this.needResponseLoading = false;
        });
    },
    checkNeedResponse() {
      if (!this.isAdmin) return;
      // For the original post, we need to check the topic
      const isOriginalPost = this.comment.cid === 'op';
      const postType = isOriginalPost ? 'topic' : 'comment';
      const topicId = this.$route.params.nid;
      const commentId = isOriginalPost ? null : this.comment.cid;
      
      // Get the current need response status from the comment object if available
      if (isOriginalPost && this.comment.needs_response !== undefined) {
        this.needsResponse = !!this.comment.needs_response;
        return;
      } else if (!isOriginalPost && this.comment.field_need_response && 
                 this.comment.field_need_response.und && 
                 this.comment.field_need_response.und[0]) {
        this.needsResponse = this.comment.field_need_response.und[0].value === '1';
        return;
      }
      
      // If we don't have the status in the comment object, fetch it from the server
      this.needResponseLoading = true;
      
      // Create the request parameters
      const params = {
        type: postType,
        topic_id: topicId
      };
      
      if (commentId) {
        params.comment_id = commentId;
      }
      
      // Fetch the need response status
      axios.get('/forum/api/need-response/check', { params })
        .then(response => {
          if (response.data && response.data.success) {
            this.needsResponse = response.data.needsResponse;
            
            // Update the comment object for future reference
            if (isOriginalPost && this.comment) {
              this.comment.needs_response = this.needsResponse;
            } else if (!isOriginalPost && this.comment) {
              if (!this.comment.field_need_response) {
                this.comment.field_need_response = { und: [{ value: '0' }] };
              }
              this.comment.field_need_response.und[0].value = this.needsResponse ? '1' : '0';
            }
          }
        })
        .catch(error => {
          console.error('Error fetching need response status:', error);
        })
        .finally(() => {
          this.needResponseLoading = false;
        });
    },
    toggleNestedQuote(index) {
      this.$set(this.expandedNestedQuotes, index, !this.expandedNestedQuotes[index]);
    },
    isNestedExpanded(index) {
      return this.expandedNestedQuotes[index] || false;
    },
    goToMessage(cid) {
      this.$emit('goto-quoted-message', cid);
    },
    getRemainingQuotes(startIndex) {
      return this.nestedQuoteChain.slice(startIndex);
    }
  },
  created() {
    // If this is the OP and we don't have nid, try to get it from the route
    if (this.comment.cid === 'op' && !this.comment.nid && this.$route && this.$route.params && this.$route.params.nid) {
      this.comment.nid = this.$route.params.nid;
    }
  },
  mounted() {
    // Fetch votes when component is mounted
    this.fetchVotes();
    document.addEventListener('click', this.closeMenu);
    
    // Check if this comment needs response
    this.checkNeedResponse();

    // Check if the quote needs a "read more" button
    if (this.parentComment) {
      this.checkQuoteOverflow();
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu);
  }
};
</script>

<style scoped>
.comment-item {
  background-color: var(--color-surface);
  padding: 20px;
  margin-bottom: 20px;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

.author-comment {
  border-left: 3px solid var(--color-highlight);
}

.author-badge {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  font-size: 0.7em;
  padding: 2px 5px;
  border-radius: 3px;
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
  height: 1.4em;
  line-height: 1;
}

/* Modern quote container - no double borders */
.comment-quote-container {
  margin-bottom: 16px;
  position: relative;
}

.quote-content {
  position: relative;
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.02));
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.quote-content:not(.expanded) .nested-quote-chain {
  max-height: 400px;
  overflow: hidden;
  position: relative;
}

.quote-content.expanded .nested-quote-chain,
.quote-content.has-expanded-nested .nested-quote-chain {
  max-height: none;
  overflow: visible;
}



.toggle-quote-btn {
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 0.8em;
  padding: 6px 12px;
  margin: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: block;
  width: calc(100% - 16px);
  text-align: center;
}

.toggle-quote-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border-color: var(--color-highlight);
}

.quote-fallback {
  padding: 12px 16px;
  color: var(--color-text-secondary);
  font-style: italic;
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.02));
  border-radius: 8px;
  margin: 0;
  text-align: center;
}

.comment-header {
  display: flex;
  margin-bottom: 15px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-outline);
  align-items: center;
}

.profile-pic {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background);
}

.profile-pic img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.default-avatar {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-active-surface);
  color: var(--color-text-secondary);
}

.default-avatar i {
  font-size: 28px;
}

.user-info {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.user-name-container {
  display: flex;
  align-items: center;
}

.comment-author {
  font-weight: 600;
  color: var(--color-highlight);
  font-size: 1.05rem;
  display: flex;
  align-items: center;
}

/* New reputation indicator styles */
.reputation-indicator {
  display: flex;
  gap: 4px;
}

.reputation-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-outline);
  transition: all 0.2s ease;
}

.reputation-dot.filled {
  background-color: #4caf50;
}

.reputation-dot.filled.negative {
  background-color: #f44336;
}

/* Reputation classes for the container */
.reputation-high .reputation-dot.filled {
  background-color: #4caf50;
}

.reputation-positive .reputation-dot.filled {
  background-color: #8bc34a;
}

.reputation-neutral .reputation-dot.filled {
  background-color: #9e9e9e;
}

.reputation-negative .reputation-dot.filled {
  background-color: #ff9800;
}

.reputation-low .reputation-dot.filled {
  background-color: #f44336;
}

.comment-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
  gap: 6px;
}

.comment-date {
  color: var(--color-text-secondary);
}

.comment-views {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-secondary);
  font-size: 0.85em;
  margin-top: 2px;
}

.views-icon {
  font-size: 14px;
  opacity: 0.7;
}

.comment-actions-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-number {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.comment-body {
  white-space: pre-line;
  line-height: 1.6;
  margin-top: 15px;
  padding-left: 0;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

/* Add highlight effect for when a comment is referenced */
.highlight-comment {
  animation: highlight-pulse 2s ease;
}

@keyframes highlight-pulse {
  0% { box-shadow: 0 0 0 0 rgba(var(--color-highlight-rgb, 33, 150, 243), 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(var(--color-highlight-rgb, 33, 150, 243), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--color-highlight-rgb, 33, 150, 243), 0); }
}

/* Add styles for the reply button */
.comment-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid var(--color-outline);
}

.voting-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vote-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  color: var(--color-text-secondary);
  padding: 5px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.vote-btn:hover:not(:disabled) {
  background-color: var(--color-active-surface);
}

.vote-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.vote-btn.active {
  color: var(--color-highlight);
}

.vote-btn.upvote.active {
  color: #4caf50;
}

.vote-btn.downvote.active {
  color: #f44336;
}

.vote-count {
  font-size: 0.9em;
  font-weight: 500;
}

.total-score {
  font-weight: bold;
  font-size: 0.9em;
}

.positive-score {
  color: #4caf50;
}

.negative-score {
  color: #f44336;
}

.reply-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.reply-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

/* Three dots menu styles */
.comment-menu-container {
  position: relative;
}

.menu-trigger {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.menu-trigger:hover {
  background-color: var(--color-active-surface);
  color: var(--color-highlight);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--color-surface);
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 150px;
  padding: 8px 0;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 8px 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-primary);
  transition: background-color 0.2s ease;
}

.menu-item:hover {
  background-color: var(--color-active-surface);
}

.menu-item.delete {
  color: #f44336;
}

.menu-item.delete:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.menu-item i {
  font-size: 18px;
}

/* Edit form styles */
.edit-form {
  margin-top: 15px;
  border-top: 1px solid var(--color-outline);
  padding-top: 15px;
  width: 100%; /* Ensure the form takes full width */
  box-sizing: border-box; /* Include padding in width calculation */
}

.edit-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  font-family: inherit;
  resize: vertical; /* Only allow vertical resizing */
  margin-bottom: 10px;
  box-sizing: border-box; /* Include padding in width calculation */
  max-width: 100%; /* Prevent overflow */
  overflow-x: auto; /* Add horizontal scrolling if content is too wide */
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-btn, .save-btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.cancel-btn {
  background-color: transparent;
  border: 1px solid var(--color-outline);
  color: var(--color-text-primary);
}

.cancel-btn:hover {
  background-color: var(--color-active-surface);
}

.save-btn {
  background-color: var(--color-highlight);
  border: none;
  color: var(--color-on-highlight, white);
}

.save-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

/* Need Response Button styles */
.need-response-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: transparent;
  border: 1px solid var(--color-outline);
  color: var(--color-text-primary);
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 10px;
}

.need-response-btn:hover:not(:disabled) {
  background-color: var(--color-active-surface);
}

.need-response-btn.active {
  background-color: #e3f2fd;
  color: #2196f3;
  border-color: #2196f3;
}

.need-response-btn.active i {
  color: #2196f3;
}

.need-response-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

/* Image display styles */
.comment-images {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.image-container {
  max-width: 100%;
}

.attached-image {
  max-width: 600px;
  max-height: 400px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  object-fit: contain;
}

.image-caption {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #666;
  font-style: italic;
}

/* Style images within comment content (new WYSIWYG uploaded images) */
.comment-body-content img {
  max-width: 600px;
  max-height: 400px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  margin: 0.5rem 0;
  object-fit: contain;
}

a, a:visited {
  color: var(--color-highlight) !important;
  text-decoration: none;
}

/* Deep selectors for v-html content styling */
.comment-body-content ::v-deep p {
  margin: 0.8em 0;
  color: var(--color-text-primary);
}

.comment-body-content ::v-deep h1,
.comment-body-content ::v-deep h2,
.comment-body-content ::v-deep h3,
.comment-body-content ::v-deep h4,
.comment-body-content ::v-deep h5,
.comment-body-content ::v-deep h6 {
  color: var(--color-text-primary);
  margin: 1em 0 0.5em 0;
  font-weight: 600;
}

.comment-body-content ::v-deep a,
.comment-body-content ::v-deep a:visited {
  color: var(--color-highlight) !important;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.comment-body-content ::v-deep a:hover {
  border-bottom-color: var(--color-highlight);
  text-decoration: none;
}

.comment-body-content ::v-deep ul,
.comment-body-content ::v-deep ol {
  margin: 0.8em 0;
  padding-left: 1.5em;
  color: var(--color-text-primary);
}

.comment-body-content ::v-deep li {
  margin: 0.3em 0;
  color: var(--color-text-primary);
}

/* Base blockquote styling for comment content */
.comment-body-content ::v-deep blockquote {
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.05));
  border-left: 4px solid var(--color-highlight);
  margin: 1em 0;
  padding: 0.8em 1em;
  border-radius: 4px;
  color: var(--color-text-primary);
}

/* Nested blockquotes within comment body (for saved quotes) */
.comment-body-content ::v-deep blockquote blockquote {
  background-color: var(--color-background, rgba(255, 255, 255, 0.5));
  border-left: 3px solid var(--color-text-secondary, #666);
  margin: 0.5em 0;
  padding: 0.5em 0.8em;
  font-size: 0.9em;
  border-radius: 3px;
  opacity: 0.8;
}

/* Nested quote chain styling - modern card-like approach */
.nested-quote-chain {
  position: relative;
}

.nested-quote-level {
  margin: 0;
  padding: 16px;
  background: transparent;
  border: none;
  border-radius: 0;
  font-size: 0.9em; /* Keep consistent readable size */
  position: relative;
  transition: all 0.2s ease;
}

.nested-quote-level::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--color-highlight);
  border-radius: 0 2px 2px 0;
}

.nested-quote-level.quote-level-0 {
  background-color: var(--color-background, rgba(255, 255, 255, 0.8));
  border-bottom: 1px solid var(--color-outline, rgba(0, 0, 0, 0.1));
}

.nested-quote-level.quote-level-1 {
  background-color: var(--color-background, rgba(255, 255, 255, 0.6));
  opacity: 0.95;
}

.nested-quote-level.quote-level-1::before {
  background: var(--color-text-secondary, #666);
  width: 3px;
}

.nested-quote-level.quote-level-2 {
  background-color: var(--color-background, rgba(255, 255, 255, 0.4));
  opacity: 0.9;
}

.nested-quote-level.quote-level-2::before {
  background: var(--color-outline, #999);
  width: 3px;
}

/* Deeper nesting levels - keep readable */
.nested-quote-level.quote-level-3 {
  background-color: var(--color-background, rgba(255, 255, 255, 0.2));
  opacity: 0.85;
}

.nested-quote-level.quote-level-3::before {
  background: var(--color-text-secondary, #aaa);
  width: 3px;
}

.nested-quote-level.quote-level-4 {
  background-color: var(--color-background, rgba(255, 255, 255, 0.1));
  opacity: 0.8;
}

.nested-quote-level.quote-level-4::before {
  background: var(--color-outline, #ccc);
  width: 3px;
}

/* Ensure deep levels don't cycle back to blue */
.nested-quote-level.quote-level-5::before,
.nested-quote-level.quote-level-6::before,
.nested-quote-level.quote-level-7::before,
.nested-quote-level.quote-level-8::before,
.nested-quote-level.quote-level-9::before {
  background: var(--color-outline, #ddd);
  width: 3px;
}

.nested-quote-level .quote-author {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.quote-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--color-highlight, #2196f3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quote-level-1 .quote-avatar {
  background: var(--color-text-secondary, #666);
}

.quote-level-2 .quote-avatar {
  background: var(--color-outline, #999);
}

.quote-level-3 .quote-avatar {
  background: var(--color-text-secondary, #aaa);
}

.quote-level-4 .quote-avatar {
  background: var(--color-outline, #ccc);
}

/* Ensure avatar colors don't cycle back to blue */
.quote-level-5 .quote-avatar,
.quote-level-6 .quote-avatar,
.quote-level-7 .quote-avatar,
.quote-level-8 .quote-avatar,
.quote-level-9 .quote-avatar {
  background: var(--color-outline, #ddd);
}

.quote-icon {
  color: white;
  font-size: 12px;
  line-height: 1;
}

.quote-author-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-grow: 1;
}

.quote-author-info strong {
  color: var(--color-text-primary);
  font-weight: 500;
}

.nested-quote-level .quote-body-content {
  color: var(--color-text-primary);
  line-height: 1.4;
  margin-bottom: 8px;
}

.goto-message-btn {
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 0.75em;
  transition: all 0.2s ease;
  opacity: 0.8;
}

.goto-message-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border-color: var(--color-highlight);
  opacity: 1;
}

.goto-message-btn i {
  font-size: 12px;
}

.toggle-nested-btn {
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 0.75em;
  padding: 8px 12px;
  margin: 8px 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  justify-content: center;
}

.toggle-nested-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border-color: var(--color-highlight);
}

/* Nested quote chain styling - modern card-like approach */
.nested-quote-chain {
  position: relative;
}

/* Nested blockquotes within quotes - provide visual distinction */
.comment-quote .comment-body-content ::v-deep blockquote {
  background-color: var(--color-background, rgba(255, 255, 255, 0.5));
  border-left: 3px solid var(--color-text-secondary, #666);
  margin: 0.5em 0;
  padding: 0.5em 0.8em;
  font-size: 0.9em;
  border-radius: 3px;
  opacity: 0.8;
}

/* Deeply nested blockquotes (quotes within quotes within quotes) */
.comment-quote .comment-body-content ::v-deep blockquote blockquote {
  background-color: var(--color-outline, rgba(0, 0, 0, 0.1));
  border-left: 2px solid var(--color-text-secondary, #999);
  margin: 0.3em 0;
  padding: 0.3em 0.6em;
  font-size: 0.85em;
  opacity: 0.7;
}

.comment-body-content ::v-deep code {
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.1));
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  color: var(--color-text-primary);
}

.comment-body-content ::v-deep pre {
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.05));
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  padding: 1em;
  overflow-x: auto;
  margin: 1em 0;
}

.comment-body-content ::v-deep pre code {
  background-color: transparent;
  padding: 0;
  font-size: 0.875em;
}

.comment-body-content ::v-deep strong,
.comment-body-content ::v-deep b {
  font-weight: 600;
  color: var(--color-text-primary);
}

.comment-body-content ::v-deep em,
.comment-body-content ::v-deep i {
  font-style: italic;
  color: var(--color-text-primary);
}

.comment-body-content ::v-deep img {
  max-width: 600px;
  max-height: 400px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0.5rem 0;
  cursor: pointer;
  transition: transform 0.2s ease;
  object-fit: contain;
}

.comment-body-content ::v-deep img:hover {
  transform: scale(1.02);
}

.comment-body-content ::v-deep table {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
  color: var(--color-text-primary);
}

.comment-body-content ::v-deep th,
.comment-body-content ::v-deep td {
  border: 1px solid var(--color-outline);
  padding: 0.5em;
  text-align: left;
}

.comment-body-content ::v-deep th {
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.05));
  font-weight: 600;
}

.comment-body-content ::v-deep hr {
  border: none;
  height: 1px;
  background-color: var(--color-outline);
  margin: 1.5em 0;
}

/* Additional base styling for the content container */
.comment-body-content {
  line-height: 1.6;
  word-wrap: break-word;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

/* Responsive adjustments for v-html content */
@media (max-width: 768px) {
  .comment-body-content {
    font-size: 0.9em;
  }
  
  .comment-body-content ::v-deep pre {
    padding: 0.5em;
    font-size: 0.8em;
  }
  
  .comment-body-content ::v-deep table {
    font-size: 0.85em;
  }
  
  /* Make images more responsive on mobile */
  .attached-image,
  .comment-body-content img,
  .comment-body-content ::v-deep img {
    max-width: 100%;
    max-height: 300px;
  }
}
</style>
